// AutoEDA.jsx  ─  ai.degenden.xyz
// ─────────────────────────────────────────────────────────────────────────────
// Full Automated EDA — one upload, everything runs.
//
// WHAT THIS IS:
//   A standalone FEATURE (not an experiment) that takes a CSV and renders a
//   complete professional EDA by orchestrating every real lab you already built.
//   No mocks. No simplified versions. The exact same CleaningLab, DistributionLab,
//   StatisticsLab, CorrelationLab, DashboardEngine, and ReportComposer that exist
//   in the experiment workspace — but wired together with a shared live dataset,
//   a profiling engine, and a master tab shell.
//
// DATA FLOW:
//   Upload ──► profileDataset() ──► ds (shared React state)
//                                        │
//           ┌──────────────────────────────────────────────┐
//           │                                              │
//        CleaningLab.onUpdate(newDs)                       │
//           │                                              │
//           └──► setDs(newDs) ──► ALL tabs re-render  ◄───┘
//                              with cleaned data
//
// TABS (real labs, zero mocks):
//   1. PROFILE      ─ instant quality score, full column profiler, null heatmap,
//                     distribution shapes, outlier flags, schema cards
//   2. CLEANING     ─ full CleaningLab  (11 ops, undo/redo, version history, log)
//   3. DISTRIBUTIONS─ full DistributionLab (all-cols overview + single deep-dive)
//   4. STATISTICS   ─ full StatisticsLab (descriptive + inferential + compare)
//   5. CORRELATIONS ─ full CorrelationLab (heatmap, top+/top−, scatter gallery)
//   6. VISUALIZATION ─ DashboardEngine (auto + custom + pinned draggable dashboard)
//   7. QUALITY     ─ auto-written plain-English analysis (pure JS, no LLM)
//   8. REPORT       ─ full ReportComposer (drag sections, notes, HTML/MD export)
//
// PROFILING ENGINE (runs once per upload, pure JS):
//   ─ null counts, null%, duplicate count, complete-row%
//   ─ per-column: type, unique count, stats (mean/std/q1/q3/skew/outliers)
//   ─ distribution shapes (normal / right-skew / left-skew)
//   ─ feature flags (ID col, constant, high cardinality, heavy null)
//   ─ top-N correlations (Pearson, capped at 12×12 matrix)
//   ─ quality score 0–100
//   ─ auto-insight bullets
//
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart, Bar, AreaChart, Area, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend,
} from 'recharts';
import {
  C, PALETTE,
  parseCSV, inferTypes, colStats,
  getHistogram, getValueCounts, pearson,
  ANALYTICS_CAP,
  PinContext,
} from './labs/wsShared';

// ── Real labs — exact same components used in experiments ────────────────────
import CleaningLab     from './labs/CleaningLab';
import DistributionLab from './labs/DistributionLab';
import StatisticsLab   from './labs/StatisticsLab';
import CorrelationLab  from './labs/CorrelationLab';
import DashboardEngine, { buildAutoCharts, computeAggrData } from './labs/DashboardEngine';
import ReportComposer  from './labs/ReportComposer';
import DataTable       from './labs/DataTable';
import { addWorkspaceItem, getAuthUserKey } from './data/workspaceStore';
import { idbSave, idbLoad, validateRowHeaders } from './data/idbStore';
import { getWorkspaceActionSummary } from './data/workspaceVersioning';
import { workspace as workspaceApi } from '../api/client';
import { startUsageSession, usageStep, usageWorkflowFromTabs } from '../utils/usageTracker';
import { trackAction } from '../utils/actionStore';
import PublishExperimentModal from './ui/PublishExperimentModal';
import HFDatasetPicker from './HFDatasetPicker';

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const AUTO_EDA_IDB_KEY = 'auto_eda:local';

function fmtBytes(b) {
  if (!b) return '—';
  if (b >= 1_048_576) return (b / 1_048_576).toFixed(1) + ' MB';
  if (b >= 1024)      return Math.round(b / 1024) + ' KB';
  return b + ' B';
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════
const SAMPLES = [
  { name:'Iris',     icon:'🌸', desc:'150 × 5 · classification baseline',     url:'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/iris.csv'        },
  { name:'Titanic',  icon:'🚢', desc:'891 × 12 · survival analysis',           url:'https://raw.githubusercontent.com/datasciencedojo/datasets/master/titanic.csv' },
  { name:'Tips',     icon:'💰', desc:'244 × 7 · regression + categoricals',    url:'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/tips.csv'        },
  { name:'Diamonds', icon:'💎', desc:'53,940 × 10 · price prediction',         url:'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/diamonds.csv'    },
  { name:'Penguins', icon:'🐧', desc:'344 × 7 · multi-class classification',   url:'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/penguins.csv'    },
  { name:'MPG',      icon:'🚗', desc:'398 × 9 · fuel efficiency regression',   url:'https://raw.githubusercontent.com/mwaskom/seaborn-data/master/mpg.csv'         },
];

const TABS = [
  { id:'profile',       label:'PROFILE',       icon:'🔬', color:C.cyan                          },
  { id:'cleaning',      label:'CLEANING',      icon:'🧹', color:C.green                         },
  { id:'distributions', label:'DISTRIBUTIONS', icon:'📊', color:C.violet                        },
  { id:'statistics',    label:'STATISTICS',    icon:'📐', color:'#fbbf24'                        },
  { id:'correlations',  label:'CORRELATIONS',  icon:'🔗', color:C.orange                        },
  { id:'visualization', label:'VISUALIZATION', icon:'⬟',  color:C.pink                          },
  { id:'quality',       label:'QUALITY',       icon:'💡', color:'#34d399'                        },
  { id:'report',        label:'REPORT',        icon:'📄', color:C.t3                            },
];

// ═══════════════════════════════════════════════════════════════════════════
// PROFILING ENGINE  (synchronous, pure JS)
// ═══════════════════════════════════════════════════════════════════════════
const USAGE_STEP_BY_TAB = {
  profile: 'profile',
  cleaning: 'cleaning',
  distributions: 'distributions',
  statistics: 'statistics',
  correlations: 'correlations',
  visualization: 'visualization',
  quality: 'quality',
  report: 'report',
};

const AUTO_EDA_USAGE_WORKFLOW = usageWorkflowFromTabs(TABS, { includeUpload: true });

function profileDataset(ds) {
  const { rows, headers } = ds;
  if (!rows?.length || !headers?.length) return null;

  // 1. Types (inferTypes now samples internally to 10K for speed)
  const types   = inferTypes(headers, rows);
  const numCols = types.filter(t => t.type === 'numeric').map(t => t.col);
  const catCols = types.filter(t => t.type === 'categorical').map(t => t.col);

  // 2. Null counts — single pass over full rows (accurate counts matter for QA)
  const nullMap = {};
  headers.forEach(h => { nullMap[h] = 0; });
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    for (let j = 0; j < headers.length; j++) {
      const v = r[headers[j]];
      if (v === '' || v == null) nullMap[headers[j]]++;
    }
  }
  const totalNulls = Object.values(nullMap).reduce((s,n)=>s+n, 0);
  const nullPct    = totalNulls > 0 ? +((totalNulls / (rows.length * headers.length)) * 100).toFixed(2) : 0;

  // 3. Duplicates — capped at 200K rows to avoid building 1M-element string Set
  const DUP_CAP  = Math.min(rows.length, 200_000);
  const rowKeys  = [];
  for (let i = 0; i < DUP_CAP; i++) rowKeys.push(headers.map(h => rows[i][h]).join('\x00'));
  const dupCount = rowKeys.length - new Set(rowKeys).size;

  // 4. Per-column deep profile
  // colStats / getHistogram / getValueCounts all sample internally (ANALYTICS_CAP = 50K)
  const colProfiles = headers.map(h => {
    const t      = types.find(t => t.col === h);
    const nullN  = nullMap[h];
    const nullP  = +((nullN / rows.length) * 100).toFixed(1);
    const st     = t?.type === 'numeric' ? colStats(rows, h) : null;
    const vc     = t?.type === 'categorical' ? getValueCounts(rows, h, 8) : null;
    const hist   = t?.type === 'numeric' ? getHistogram(rows, h, 16) : null;

    // Feature flags — use sampleSize-adjusted ratios since inferTypes now samples
    const sampleSize = t?.sampleSize || rows.length;
    const isId       = t?.type === 'numeric' && t.uniqueCount / sampleSize > 0.92 && rows.length > 30;
    const isConst    = t?.uniqueCount <= 1;
    const isHighCard = t?.type === 'categorical' && t.uniqueCount > sampleSize * 0.5 && rows.length > 50;
    const isBinary   = t?.uniqueCount === 2;
    const flags = [
      isConst    && { label:'CONSTANT',    color:C.red    },
      isId       && { label:'LIKELY ID',   color:'#fbbf24'},
      isHighCard && { label:'HIGH CARD',   color:'#fbbf24'},
      isBinary   && { label:'BINARY',      color:C.cyan   },
      nullP > 50 && { label:`${nullP}% NULL`, color:C.red  },
      nullP > 10 && nullP <= 50 && { label:`${nullP}% NULL`, color:'#fbbf24' },
    ].filter(Boolean);

    // Shape — reuse the sampled vals already computed inside colStats rather than
    // re-scanning all rows again. We approximate kurtosis from the same sample.
    let shape = null;
    if (st) {
      // Use ANALYTICS_CAP sample for kurtosis (same sample colStats used internally)
      const step    = rows.length > ANALYTICS_CAP ? Math.ceil(rows.length / ANALYTICS_CAP) : 1;
      const rawKurt = (() => {
        let sum = 0, cnt = 0;
        for (let i = 0; i < rows.length; i += step) {
          const v = parseFloat(rows[i][h]);
          if (!isNaN(v) && isFinite(v)) { sum += Math.pow((v - st.mean) / Math.max(st.std, 1e-9), 4); cnt++; }
        }
        return cnt > 3 ? sum / cnt - 3 : 0;
      })();
      shape = {
        skewLabel: Math.abs(st.skew)<0.5 ? 'Normal' : st.skew>0 ? 'Right-skew' : 'Left-skew',
        kurtosis: +rawKurt.toFixed(3),
        outlierPct: +((st.outlierCount/Math.max(st.count,1))*100).toFixed(1),
        cv: st.mean !== 0 ? +(Math.abs(st.std/st.mean)*100).toFixed(1) : null,
      };
    }

    return { col:h, type:t?.type||'?', uniqueCount:t?.uniqueCount||0, nullCount:nullN, nullPct:nullP, stats:st, valueCounts:vc, hist, shape, flags };
  });

  // 5. Top correlations (capped at 12×12 to avoid UI lag)
  const corrCols = numCols.slice(0, 12);
  const corrSample = rows.slice(0, 3000);
  const topCorr = [];
  for (let i = 0; i < corrCols.length; i++)
    for (let j = i+1; j < corrCols.length; j++) {
      const r = pearson(corrSample, corrCols[i], corrCols[j]);
      topCorr.push({ a:corrCols[i], b:corrCols[j], r, abs:Math.abs(r) });
    }
  topCorr.sort((a,b)=>b.abs-a.abs);

  // 6. Quality score
  const qualityScore = Math.max(0, Math.round(
    100
    - nullPct * 1.2
    - (dupCount / Math.max(rows.length,1)) * 25
    - colProfiles.filter(p=>p.flags.some(f=>f.label==='CONSTANT')).length * 5
    - colProfiles.filter(p=>p.nullPct > 30).length * 3
    - (numCols.length === 0 ? 10 : 0)
  ));

  // 7. Auto quality (plain English, no LLM)
  const quality = buildQuality({ nullPct, dupCount, topCorr, colProfiles, numCols, catCols, rows, headers });

  return {
    types, numCols, catCols,
    nullMap, nullPct, dupCount, totalNulls,
    colProfiles, topCorr, quality, qualityScore,
    meta: {
      rows:    rows.length,
      cols:    headers.length,
      numeric: numCols.length,
      categorical: catCols.length,
      completeRows: rows.filter(r=>headers.every(h=>r[h]&&r[h]!=='')).length,
    },
  };
}

function buildQuality({ nullPct, dupCount, topCorr, colProfiles, numCols, catCols, rows, headers }) {
  const out = [];

  // Completeness
  if (nullPct === 0)
    out.push({ icon:'✓', color:C.green,   text:'Perfect data completeness — no null values anywhere in the dataset.' });
  else if (nullPct > 20)
    out.push({ icon:'⚠', color:C.red,     text:`High missingness: ${nullPct}% of all cells are null. Run Cleaning → Fill Nulls or Drop Null Rows before analysis.` });
  else
    out.push({ icon:'⚠', color:'#fbbf24', text:`Moderate nulls detected (${nullPct}%). Inspect affected columns before modelling.` });

  // Duplicates
  if (dupCount > 0)
    out.push({ icon:'⚠', color:'#fbbf24', text:`${dupCount.toLocaleString()} duplicate rows found. Use Cleaning → Remove Duplicates.` });
  else
    out.push({ icon:'✓', color:C.green,   text:'No duplicate rows — dataset is unique at row level.' });

  // Correlations
  topCorr.slice(0, 4).forEach(p => {
    const s = Math.abs(p.r) > 0.7 ? 'strong' : 'moderate';
    const d = p.r > 0 ? 'positive' : 'negative';
    out.push({ icon:'📈', color:C.cyan, text:`${s[0].toUpperCase()+s.slice(1)} ${d} correlation (r = ${p.r.toFixed(3)}) between "${p.a}" and "${p.b}".` });
  });

  // Skew
  const highSkew = colProfiles.filter(p => p.shape && Math.abs(p.stats?.skew||0) > 1);
  if (highSkew.length)
    out.push({ icon:'📊', color:C.violet, text:`${highSkew.length} column(s) are heavily skewed: ${highSkew.map(p=>p.col).join(', ')}. Log-transform may improve model performance.` });

  // Outliers
  const highOut = colProfiles.filter(p => p.shape?.outlierPct > 5);
  if (highOut.length)
    out.push({ icon:'🎯', color:'#fb923c', text:`High outlier density in: ${highOut.map(p=>`${p.col} (${p.shape.outlierPct}%)`).join(', ')}.` });

  // Feature issues
  const idCols   = colProfiles.filter(p => p.flags.some(f=>f.label==='LIKELY ID'));
  const consts   = colProfiles.filter(p => p.flags.some(f=>f.label==='CONSTANT'));
  const hiCards  = colProfiles.filter(p => p.flags.some(f=>f.label==='HIGH CARD'));

  if (idCols.length)
    out.push({ icon:'🔑', color:'#fbbf24', text:`Likely ID/key columns: ${idCols.map(p=>p.col).join(', ')}. Drop before training any model.` });
  if (consts.length)
    out.push({ icon:'✗', color:C.red,     text:`Zero-variance columns: ${consts.map(p=>p.col).join(', ')}. These carry no information — drop them.` });
  if (hiCards.length)
    out.push({ icon:'⚠', color:'#fbbf24', text:`High-cardinality categoricals: ${hiCards.map(p=>p.col).join(', ')}. Consider target-encoding or grouping rare categories.` });

  // Class imbalance check
  const heavyImbalance = catCols.find(col => {
    const counts = Object.values(rows.reduce((m,r)=>{ const v=r[col]; m[v]=(m[v]||0)+1; return m; },{}));
    if (!counts.length) return false;
    return Math.max(...counts) / rows.length > 0.8;
  });
  if (heavyImbalance)
    out.push({ icon:'⚠', color:'#fbbf24', text:`"${heavyImbalance}" has severe class imbalance — one category dominates >80% of rows. Consider resampling for classification tasks.` });

  // Dataset scale
  if (rows.length > 50000)
    out.push({ icon:'⚡', color:C.cyan, text:`Large dataset (${(rows.length/1000).toFixed(0)}k rows). Charts are automatically sampled for performance; full data is used in all statistics.` });

  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// SHARED ATOMS
// ═══════════════════════════════════════════════════════════════════════════
const Pill = ({ label, color=C.cyan, size=8 }) => (
  <span style={{ fontSize:size, padding:'2px 6px', background:`${color}18`, border:`1px solid ${color}40`, borderRadius:2, color, fontFamily:'JetBrains Mono,monospace', fontWeight:700, whiteSpace:'nowrap' }}>{label}</span>
);

const MetCard = ({ label, value, color=C.cyan, sub, large }) => (
  <div style={{ padding:'12px 14px', background:C.raised, border:`1px solid ${C.b1}`, borderLeft:`2px solid ${color}`, borderRadius:3 }}>
    <div style={{ fontSize:7.5, color:C.t4, letterSpacing:'.12em', fontFamily:'JetBrains Mono,monospace', textTransform:'uppercase' }}>{label}</div>
    <div style={{ fontFamily:'Syne,sans-serif', fontWeight:900, fontSize:large?26:20, color, lineHeight:1.1, marginTop:2 }}>{value}</div>
    {sub && <div style={{ fontSize:9, color:C.t3, marginTop:2 }}>{sub}</div>}
  </div>
);

const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.b2}`, padding:'8px 12px', borderRadius:4, fontSize:11 }}>
      <div style={{ color:C.t3, marginBottom:3, fontFamily:'JetBrains Mono,monospace', fontSize:9 }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ color:p.color||C.cyan }}>{p.name}: <b>{typeof p.value==='number'?p.value.toFixed(4):p.value}</b></div>
      ))}
    </div>
  );
};

function QualityRing({ score, size=80 }) {
  const color = score>=80?C.green:score>=60?'#fbbf24':C.red;
  const r=size*.37, cx=size/2, cy=size/2, c=2*Math.PI*r;
  return (
    <svg width={size} height={size} style={{ flexShrink:0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={`${color}22`} strokeWidth={size*.1}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={size*.1}
        strokeDasharray={`${c*(score/100)} ${c*(1-score/100)}`} strokeDashoffset={c*.25} strokeLinecap="round"
        style={{ transition:'stroke-dasharray .6s ease' }}/>
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
        fill={color} fontSize={size*.21} fontWeight="900" fontFamily="Syne,sans-serif">{score}</text>
    </svg>
  );
}

const SectionHead = ({ label, color=C.cyan }) => (
  <div style={{ fontSize:9, color, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.13em', fontWeight:700, marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
    <div style={{ height:1, width:18, background:color, opacity:.5 }}/>
    {label}
    <div style={{ flex:1, height:1, background:`linear-gradient(90deg,${color}44,transparent)` }}/>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// TAB 1 — PROFILE  (instant deep profiling, not just basics)
// ═══════════════════════════════════════════════════════════════════════════
function ProfileTab({ ds, profile }) {
  const { meta, qualityScore, colProfiles, nullMap, nullPct, dupCount, topCorr } = profile;
  const [view, setView] = useState('sample'); // schema | nullmap | sample | correlations

  const nullData = ds.headers
    .map(h => ({ col:h.length>16?h.slice(0,14)+'…':h, full:h, nulls:nullMap[h], pct:+((nullMap[h]/ds.rows.length)*100).toFixed(1) }))
    .sort((a,b)=>b.nulls-a.nulls).filter(d=>d.nulls>0);

  const typeChartData = [
    { name:'Numeric',     value:meta.numeric,     fill:C.cyan   },
    { name:'Categorical', value:meta.categorical, fill:'#fbbf24' },
  ].filter(d=>d.value>0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* ── Hero strip ── */}
      <div style={{ display:'grid', gridTemplateColumns:'auto repeat(6,1fr)', gap:10, alignItems:'stretch' }}>
        <div style={{ padding:'10px 14px', background:C.raised, border:`1px solid ${C.b1}`, borderRadius:3, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4 }}>
          <QualityRing score={qualityScore} size={72}/>
          <div style={{ fontSize:7.5, color:C.t4, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.1em' }}>QUALITY</div>
        </div>
        <MetCard label="Rows"          value={meta.rows.toLocaleString()}        color={C.cyan}/>
        <MetCard label="Columns"       value={meta.cols}                          color={C.green}/>
        <MetCard label="Numeric Cols"  value={meta.numeric}                       color={C.violet}/>
        <MetCard label="Cat Cols"      value={meta.categorical}                   color='#fbbf24'/>
        <MetCard label="Null Cells"    value={`${nullPct}%`}                      color={nullPct>20?C.red:nullPct>5?'#fbbf24':C.green} sub={`${profile.totalNulls.toLocaleString()} cells`}/>
        <MetCard label="Duplicates"    value={dupCount.toLocaleString()}           color={dupCount>0?'#fbbf24':C.green}/>
      </div>

      {/* ── Sub-tabs ── */}
      <div style={{ display:'flex', gap:2, background:C.deep, border:`1px solid ${C.b0}`, borderRadius:4, padding:3, flexWrap:'wrap' }}>
        {[
          ['sample',       'DATA PREVIEW'],
          ['nullmap',      'NULL MAP'],
          ['schema',       'COLUMN SCHEMA'],
        ].map(([id,lbl]) => (
          <button key={id} onClick={()=>setView(id)} style={{ padding:'6px 14px', border:'none', borderRadius:3, cursor:'pointer', background:view===id?C.cyan:'transparent', color:view===id?C.void:C.t2, fontFamily:'JetBrains Mono,monospace', fontSize:9, fontWeight:700, letterSpacing:'.09em' }}>{lbl}</button>
        ))}
      </div>

      {/* ── COLUMN SCHEMA ── */}
      {view==='schema' && (
        <div>
          <SectionHead label="FULL COLUMN PROFILE"/>
          <div style={{ overflowX:'auto', border:`1px solid ${C.b1}`, borderRadius:4 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10, minWidth:900 }}>
              <thead>
                <tr style={{ background:C.deep }}>
                  {['#','Column','Type','Unique','Nulls','Null%','Min','Max','Mean','Std Dev','Skew','Outliers%','Flags'].map(h=>(
                    <th key={h} style={{ padding:'7px 10px', textAlign:'left', fontSize:8, color:C.t4, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.07em', fontWeight:700, borderBottom:`1px solid ${C.b1}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colProfiles.map((p,i) => (
                  <tr key={p.col} style={{ borderBottom:`1px solid ${C.b0}`, background:i%2?`${C.raised}60`:'transparent' }}>
                    <td style={{ padding:'6px 10px', color:C.t4, fontSize:9, fontFamily:'JetBrains Mono,monospace' }}>{i}</td>
                    <td style={{ padding:'6px 10px', color:C.t1, fontWeight:600, maxWidth:140, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.col}</td>
                    <td style={{ padding:'6px 10px' }}><Pill label={p.type==='numeric'?'NUM':'CAT'} color={p.type==='numeric'?C.cyan:'#fbbf24'}/></td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:C.t2 }}>{p.uniqueCount.toLocaleString()}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:p.nullCount>0?C.red:C.t4 }}>{p.nullCount.toLocaleString()}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:p.nullPct>20?C.red:p.nullPct>5?'#fbbf24':C.t4 }}>{p.nullPct}%</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:C.t3, fontSize:9 }}>{p.stats?.min??'—'}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:C.t3, fontSize:9 }}>{p.stats?.max??'—'}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:C.t2, fontSize:9 }}>{p.stats?.mean??'—'}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:C.t2, fontSize:9 }}>{p.stats?.std??'—'}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:Math.abs(p.stats?.skew||0)>1?'#fbbf24':C.t3, fontSize:9 }}>{p.stats?.skew??'—'}</td>
                    <td style={{ padding:'6px 10px', fontFamily:'JetBrains Mono,monospace', color:p.shape?.outlierPct>5?C.red:C.t3, fontSize:9 }}>{p.shape?.outlierPct??'—'}</td>
                    <td style={{ padding:'6px 10px' }}>
                      <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                        {p.flags.length===0
                          ? <Pill label="✓ CLEAN" color={C.green}/>
                          : p.flags.map((f,j)=><Pill key={j} label={f.label} color={f.color}/>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Distribution sparklines for numeric cols */}
          {profile.numCols.length > 0 && (
            <div style={{ marginTop:16 }}>
              <SectionHead label="DISTRIBUTION SHAPES — ALL NUMERIC COLUMNS" color={C.violet}/>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:10 }}>
                {colProfiles.filter(p=>p.hist).map(p=>(
                  <div key={p.col} style={{ background:C.raised, border:`1px solid ${C.b1}`, borderRadius:4, padding:'10px 12px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                      <span style={{ fontSize:9.5, color:C.t1, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, marginRight:4 }}>{p.col}</span>
                      <Pill label={p.shape?.skewLabel||'?'} color={Math.abs(p.stats?.skew||0)<0.5?C.green:'#fbbf24'} size={7}/>
                    </div>
                    <div style={{ height:48 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={p.hist} margin={{ top:0,right:0,left:0,bottom:0 }}>
                          <Bar dataKey="count" fill={C.violet} opacity={0.7} radius={[1,1,0,0]}/>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
                      <span style={{ fontSize:7.5, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>μ={p.stats?.mean}</span>
                      <span style={{ fontSize:7.5, color:p.shape?.outlierPct>5?C.red:C.t4, fontFamily:'JetBrains Mono,monospace' }}>⊹{p.shape?.outlierPct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categorical value-count previews */}
          {profile.catCols.length > 0 && (
            <div style={{ marginTop:16 }}>
              <SectionHead label="TOP VALUES — ALL CATEGORICAL COLUMNS" color='#fbbf24'/>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:10 }}>
                {colProfiles.filter(p=>p.valueCounts?.length).map(p=>(
                  <div key={p.col} style={{ background:C.raised, border:`1px solid ${C.b1}`, borderRadius:4, padding:'10px 12px' }}>
                    <div style={{ fontSize:9.5, color:C.t1, fontWeight:600, marginBottom:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.col}</div>
                    {p.valueCounts.slice(0,5).map((vc,i)=>(
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                        <span style={{ fontSize:8.5, color:C.t2, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{vc.name}</span>
                        <div style={{ width:Math.max(2,(vc.count/p.valueCounts[0].count)*60), height:4, background:PALETTE[i%PALETTE.length], borderRadius:2, flexShrink:0 }}/>
                        <span style={{ fontSize:7.5, color:C.t4, fontFamily:'JetBrains Mono,monospace', minWidth:30, textAlign:'right' }}>{vc.pct}%</span>
                      </div>
                    ))}
                    <div style={{ fontSize:7.5, color:C.t4, marginTop:4, fontFamily:'JetBrains Mono,monospace' }}>{p.uniqueCount} unique values</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── NULL MAP ── */}
      {view==='nullmap' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:16 }}>
          <div>
            <SectionHead label="NULL COUNT PER COLUMN"/>
            {nullData.length > 0 ? (
              <div style={{ height:Math.min(320, nullData.length*26+60) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={nullData} layout="vertical" margin={{ top:4,right:14,left:0,bottom:4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.b1} horizontal={false}/>
                    <XAxis type="number" tick={{ fill:C.t4, fontSize:8 }} tickFormatter={v=>`${v}`}/>
                    <YAxis type="category" dataKey="col" width={110} tick={{ fill:C.t2, fontSize:9, fontFamily:'JetBrains Mono,monospace' }}/>
                    <Tooltip content={<CTip/>} formatter={(v,n,p)=>[`${v} rows (${p.payload.pct}%)`, 'Null count']}/>
                    <Bar dataKey="nulls" radius={[0,3,3,0]} label={{ position:'right', fill:C.t4, fontSize:7.5, formatter:v=>`${v}` }}>
                      {nullData.map((d,i)=><Cell key={i} fill={d.pct>20?C.red:d.pct>5?'#fbbf24':C.green}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div style={{ padding:30, textAlign:'center', background:C.raised, borderRadius:4, border:`1px solid ${C.green}30` }}>
                <div style={{ fontSize:28, marginBottom:6 }}>✓</div>
                <div style={{ color:C.green, fontSize:12, fontWeight:600 }}>No null values in dataset</div>
              </div>
            )}
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <SectionHead label="SUMMARY"/>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {[
                ['Total null cells',    `${profile.totalNulls.toLocaleString()} / ${(ds.rows.length*ds.headers.length).toLocaleString()}`, nullPct>10?C.red:'#fbbf24'],
                ['Null %',              `${nullPct}%`,             nullPct>20?C.red:nullPct>5?'#fbbf24':C.green],
                ['Duplicate rows',      dupCount.toLocaleString(), dupCount>0?'#fbbf24':C.green],
                ['Complete rows',       `${((meta.completeRows/ds.rows.length)*100).toFixed(1)}%`, C.cyan],
                ['Cols with any null',  nullData.length.toString(), nullData.length>0?'#fbbf24':C.green],
                ['Cols >50% null',      colProfiles.filter(p=>p.nullPct>50).length.toString(), C.red],
              ].map(([lbl,val,color])=>(
                <div key={lbl} style={{ display:'flex', justifyContent:'space-between', padding:'6px 10px', background:C.raised, borderRadius:3 }}>
                  <span style={{ fontSize:9, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>{lbl}</span>
                  <span style={{ fontSize:10, color, fontFamily:'JetBrains Mono,monospace', fontWeight:700 }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={{ height:160, marginTop:4 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={typeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius="35%" outerRadius="65%" paddingAngle={3}>
                    {typeChartData.map((d,i)=><Cell key={i} fill={d.fill}/>)}
                  </Pie>
                  <Tooltip content={<CTip/>}/>
                  <Legend iconSize={8} wrapperStyle={{ fontSize:9 }}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ── SAMPLE / PREVIEW ── */}
      {view==='sample' && <DataTable rows={ds.rows} headers={ds.headers} types={profile.types} showControls/>}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB 7 — QUALITY  (full auto analysis, no LLM)
// ═══════════════════════════════════════════════════════════════════════════
function QualityTab({ ds, profile }) {
  const { quality, topCorr, colProfiles, meta, qualityScore, nullPct, dupCount } = profile;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* Summary hero */}
      <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap:18, padding:'22px 24px', background:`linear-gradient(135deg,${C.deep},#0c1620)`, border:`1px solid ${C.b1}`, borderRadius:6, alignItems:'center' }}>
        <QualityRing score={qualityScore} size={90}/>
        <div>
          <div style={{ fontSize:8, letterSpacing:'.22em', color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontWeight:700, marginBottom:8 }}>AUTO EDA SUMMARY · ai.degenden.xyz</div>
          <div style={{ fontFamily:'Syne,sans-serif', fontWeight:900, fontSize:20, color:C.t1, marginBottom:6 }}>{ds.name}</div>
          <div style={{ fontSize:10.5, color:C.t3, lineHeight:1.8 }}>
            {meta.rows.toLocaleString()} rows · {meta.cols} columns ({meta.numeric} numeric, {meta.categorical} categorical) ·
            Data quality: <span style={{ color:qualityScore>=80?C.green:qualityScore>=60?'#fbbf24':C.red, fontWeight:700 }}>{qualityScore}/100</span> ·
            {nullPct===0 ? <span style={{ color:C.green }}> complete dataset</span> : <span style={{ color:'#fbbf24' }}> {nullPct}% null cells</span>}
            {dupCount > 0 && <span style={{ color:'#fbbf24' }}> · {dupCount} duplicates</span>}
          </div>
        </div>
      </div>

      {/* Auto quality */}
      <div>
        <SectionHead label="AUTOMATED QUALITY — GENERATED FROM DATA"/>
        <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
          {quality.map((ins,i)=>(
            <div key={i} style={{ display:'flex', gap:14, padding:'12px 16px', background:`${ins.color}07`, border:`1px solid ${ins.color}28`, borderLeft:`3px solid ${ins.color}`, borderRadius:4 }}>
              <span style={{ fontSize:17, flexShrink:0, lineHeight:1.4 }}>{ins.icon}</span>
              <p style={{ margin:0, fontSize:11.5, color:C.t2, lineHeight:1.8 }}>{ins.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Feature flags summary */}
      {colProfiles.some(p=>p.flags.length>0) && (
        <div>
          <SectionHead label="FLAGGED COLUMNS" color='#fbbf24'/>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:8 }}>
            {colProfiles.filter(p=>p.flags.length>0).map(p=>(
              <div key={p.col} style={{ padding:'10px 12px', background:C.raised, border:`1px solid ${C.b1}`, borderRadius:4, display:'flex', alignItems:'center', gap:10 }}>
                <Pill label={p.type==='numeric'?'NUM':'CAT'} color={p.type==='numeric'?C.cyan:'#fbbf24'}/>
                <span style={{ fontSize:10, color:C.t1, flex:1, overflow:'hidden', textOverflow:'ellipsis' }}>{p.col}</span>
                <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                  {p.flags.map((f,i)=><Pill key={i} label={f.label} color={f.color} size={7.5}/>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top correlations visual */}
      {topCorr.length > 0 && (
        <div>
          <SectionHead label="CORRELATION RANKING" color={C.orange}/>
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            {topCorr.slice(0,8).map((p,i)=>{
              const col = Math.abs(p.r)>0.7?(p.r>0?C.green:C.red):Math.abs(p.r)>0.4?'#fbbf24':C.t3;
              return (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 12px', background:C.raised, borderRadius:3 }}>
                  <span style={{ fontSize:8, color:C.t4, fontFamily:'JetBrains Mono,monospace', minWidth:14 }}>#{i+1}</span>
                  <span style={{ fontSize:9.5, color:C.t2, flex:1 }}>{p.a} <span style={{ color:C.t4 }}>↔</span> {p.b}</span>
                  <div style={{ width:100, height:5, background:C.b1, borderRadius:3 }}>
                    <div style={{ width:`${Math.abs(p.r)*100}%`, height:'100%', background:col, borderRadius:3 }}/>
                  </div>
                  <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, fontWeight:700, color:col, minWidth:54, textAlign:'right' }}>r={p.r.toFixed(3)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Distribution shapes grid */}
      {colProfiles.filter(p=>p.shape).length > 0 && (
        <div>
          <SectionHead label="DISTRIBUTION SHAPES" color={C.violet}/>
          <div style={{ overflowX:'auto', border:`1px solid ${C.b1}`, borderRadius:4 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
              <thead>
                <tr style={{ background:C.deep }}>
                  {['Column','Shape','Skewness','Kurtosis','CV %','Outliers %'].map(h=>(
                    <th key={h} style={{ padding:'7px 12px', textAlign:'left', fontSize:8, color:C.t4, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.07em', borderBottom:`1px solid ${C.b1}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colProfiles.filter(p=>p.shape).map((p,i)=>(
                  <tr key={p.col} style={{ borderBottom:`1px solid ${C.b0}`, background:i%2?`${C.raised}40`:'transparent' }}>
                    <td style={{ padding:'6px 12px', color:C.t1, fontWeight:500 }}>{p.col}</td>
                    <td style={{ padding:'6px 12px' }}><Pill label={p.shape.skewLabel} color={Math.abs(p.stats.skew)<0.5?C.green:'#fbbf24'}/></td>
                    <td style={{ padding:'6px 12px', fontFamily:'JetBrains Mono,monospace', color:Math.abs(p.stats.skew)>1?'#fbbf24':C.t2 }}>{p.stats.skew}</td>
                    <td style={{ padding:'6px 12px', fontFamily:'JetBrains Mono,monospace', color:Math.abs(p.shape.kurtosis)>1?'#fb923c':C.t2 }}>{p.shape.kurtosis}</td>
                    <td style={{ padding:'6px 12px', fontFamily:'JetBrains Mono,monospace', color:C.t3 }}>{p.shape.cv??'—'}</td>
                    <td style={{ padding:'6px 12px', fontFamily:'JetBrains Mono,monospace', color:p.shape.outlierPct>5?C.red:C.t3 }}>{p.shape.outlierPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UPLOAD SCREEN
// ═══════════════════════════════════════════════════════════════════════════
function UploadScreen({ onFileReady, autoUrl, autoCsv }) {
  const [src,     setSrc]     = useState('file');
  const [showHF,  setShowHF]  = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState('');
  const [error,   setError]   = useState('');
  const [isDrag,  setIsDrag]  = useState(false);
  const [urlVal,  setUrlVal]  = useState('');
  const fileRef = useRef();
  const uploadTabs = [
    { id: 'upload', label: 'UPLOAD', icon: '⬆', color: C.cyan },
    ...TABS,
  ];
  const uploadBadges = { };

  // ingest: no longer parses CSV — hands raw text to parent which owns the worker
  const ingest = useCallback((text, name, sizeBytes = 0) => {
    if (!text?.trim()) { setError('Empty file or no data received'); setLoading(false); return; }
    onFileReady(text, name, sizeBytes); // parent immediately sets phase='processing'
  }, [onFileReady]);

  const processFile = useCallback(file => {
    if (!file) return;
    if (!/\.(csv|tsv|txt)$/i.test(file.name)) { setError('Only CSV / TSV files'); return; }
    setLoading(true); setLoadMsg(`Reading ${file.name}…`); setError('');
    const r = new FileReader();
    r.onload = e => ingest(file.name.endsWith('.tsv') ? e.target.result.replace(/\t/g,',') : e.target.result, file.name, file.size);
    r.readAsText(file);
  }, [ingest]);

  const fetchURL = useCallback(async url => {
    if (!url.trim()) { setError('Enter a URL'); return; }
    const name = url.split('/').pop()||'dataset.csv';
    setLoading(true); setLoadMsg(`Fetching ${name}…`); setError('');
    try {
      const res = await fetch(url.trim());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      ingest(text, name, text.length);
    } catch(e) { setError('Fetch failed: '+e.message); setLoading(false); }
  }, [ingest]);

  // Allow the right panel to one-click load a dataset (URL or raw CSV text).
  useEffect(() => {
    if (autoUrl && !loading) {
      setSrc('url');
      setUrlVal(String(autoUrl));
      fetchURL(String(autoUrl));
    }
  }, [autoUrl, loading, fetchURL]);

  useEffect(() => {
    if (autoCsv?.csvText && !loading) {
      setSrc('file');
      ingest(String(autoCsv.csvText), autoCsv.name || 'dataset.csv', autoCsv.csvText.length);
    }
  }, [autoCsv?.id, autoCsv?.csvText, autoCsv?.name, loading, ingest]);

  return (
    <div style={{ minHeight:'100vh', background:C.void, display:'flex', flexDirection:'column', position:'relative' }}>
      <div style={{ position:'sticky', top:0, zIndex:10, background:C.deep, borderBottom:`1px solid ${C.b0}`, overflow:'hidden' }}>
        <div style={{ display:'flex', flexWrap:'wrap', padding:'4px 6px', gap:2, overflowX:'hidden', overflowY:'hidden', maxWidth:'100%' }}>
          {uploadTabs.map(tab => {
            const active = tab.id === 'upload';
            const badge = uploadBadges[tab.id];
            const disabled = tab.id !== 'upload';
            return (
              <button
                key={tab.id}
                disabled={disabled}
                onClick={disabled ? undefined : () => setSrc('file')}
                style={{
                  display:'flex', alignItems:'center', gap:5,
                  padding:'8px 11px', border:'none', background:'transparent',
                  color: active ? tab.color : C.t3,
                  borderBottom:`2px solid ${active ? tab.color : 'transparent'}`,
                  fontFamily:'JetBrains Mono,monospace', fontSize:9,
                  fontWeight: active ? 700 : 400,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  whiteSpace:'nowrap', letterSpacing:'.08em',
                  transition:'all .1s',
                  position:'relative', opacity: disabled ? 0.6 : 1,
                }}
              >
                <span style={{ fontSize:12 }}>{tab.icon}</span>
                <span>{tab.label}</span>
                {badge != null && (
                  <span style={{ fontSize:7, background:C.red, color:'#fff', borderRadius:10, padding:'1px 4px', fontWeight:700, minWidth:14, textAlign:'center' }}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:40 }}>
      {/* bg glow */}
      <div style={{ position:'absolute', top:'20%', left:'50%', transform:'translateX(-50%)', width:600, height:300, background:`radial-gradient(ellipse,${C.cyan}06 0%,transparent 70%)`, pointerEvents:'none' }}/>

      <div style={{ textAlign:'center', marginBottom:40, position:'relative' }}>
        {/* <div style={{ fontSize:9, letterSpacing:'.28em', color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontWeight:700, marginBottom:12 }}>ai.degenden.xyz · AUTO EDA</div> */}
        <h1 style={{ fontFamily:'Syne,sans-serif', fontWeight:900, fontSize:38, color:C.t1, lineHeight:1.1, marginBottom:12 }}>
          Run EDA in Minutes
          <br/>
          <span style={{ background:`linear-gradient(90deg,${C.cyan},${C.violet})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>No Python setup, No LLMs</span>
        </h1>
        <p style={{ fontSize:12, color:C.t3, maxWidth:540, lineHeight:1.8, margin:'0 auto' }}>
          From Data Cleaning to Distribution to Visualization to Report Generation. No coding required, Just mouse clicks. 
          
          <br/>
          <span style={{ display:'inline-block', marginTop:6, padding:'4px 10px', borderRadius:999, background:`linear-gradient(90deg, ${C.cyan}18, ${C.violet}16)`, border:`1px solid ${C.cyan}33`, color:C.t1 }}>
            Your data never leaves your machine. 100% on Browser & 100% Privacy.
          </span>
        </p>
      </div>

      <div style={{ width:'100%', maxWidth:720, background:C.surface, border:`1px solid ${C.b1}`, borderRadius:8, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,.6)', position:'relative' }}>
        {/* tab bar */}
        <div style={{ display:'flex', background:C.deep, borderBottom:`1px solid ${C.b0}` }}>
          {[['file','📁 Upload File'],['hf','🤗 HuggingFace'],['url','🌐 From URL'],['sample','⬟ Sample Datasets']].map(([id,lbl])=>(
            <button key={id} onClick={()=>{ if(id==='hf'){setShowHF(true);return;} setSrc(id);setError(''); }} style={{ flex:1, padding:'14px', border:'none', background:(id==='hf'?showHF:src===id)?C.surface:'transparent', color:(id==='hf'?showHF:src===id)?C.cyan:C.t3, fontFamily:'JetBrains Mono,monospace', fontSize:9, fontWeight:700, cursor:'pointer', borderBottom:`2px solid ${(id==='hf'?showHF:src===id)?C.cyan:'transparent'}`, transition:'all .12s' }}>{lbl}</button>
          ))}
        </div>

        <div style={{ padding:30 }}>
          {src==='file' && (
            <div
              onDragOver={e=>{e.preventDefault();setIsDrag(true);}}
              onDragLeave={()=>setIsDrag(false)}
              onDrop={e=>{e.preventDefault();setIsDrag(false);processFile(e.dataTransfer.files[0]);}}
              onClick={()=>!loading&&fileRef.current?.click()}
              style={{ border:`2px dashed ${isDrag?C.cyan:C.b2}`, borderRadius:8, padding:'60px 20px', textAlign:'center', cursor:loading?'wait':'pointer', background:isDrag?`${C.cyan}07`:C.deep, transition:'all .2s' }}>
              <div style={{ fontSize:52, marginBottom:12, opacity:.5 }}>{loading?'⏳':'📊'}</div>
              <div style={{ fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:17, color:C.t1, marginBottom:6 }}>
                {loading ? loadMsg : 'Drop your CSV here'}
              </div>
              <div style={{ fontSize:11, color:C.t3 }}>{loading ? 'Building full EDA…' : 'or click to browse · CSV / TSV / TXT'}</div>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" style={{ display:'none' }} onChange={e=>processFile(e.target.files[0])}/>
            </div>
          )}

          {src==='url' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ fontSize:9, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>PUBLIC CSV / TSV URL</div>
              <div style={{ display:'flex', gap:8 }}>
                <input value={urlVal} onChange={e=>setUrlVal(e.target.value)} placeholder="https://example.com/data.csv"
                  onKeyDown={e=>e.key==='Enter'&&fetchURL(urlVal)}
                  style={{ flex:1, background:C.deep, border:`1px solid ${C.b2}`, color:C.t1, padding:'12px 14px', fontSize:11, fontFamily:'JetBrains Mono,monospace', borderRadius:4, outline:'none' }}/>
                <button onClick={()=>fetchURL(urlVal)} disabled={loading}
                  style={{ padding:'12px 22px', background:`${C.cyan}18`, border:`1px solid ${C.cyan}`, color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontSize:10, fontWeight:700, borderRadius:4, cursor:'pointer' }}>
                  {loading?'…':'FETCH'}
                </button>
              </div>
              {loading && <div style={{ fontSize:10, color:C.t3 }}>{loadMsg}</div>}
            </div>
          )}

          {src==='sample' && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {SAMPLES.map(s=>(
                <button key={s.name} onClick={()=>fetchURL(s.url)} disabled={loading}
                  style={{ padding:'16px', background:C.deep, border:`1px solid ${C.b1}`, borderRadius:6, cursor:'pointer', textAlign:'left', transition:'border-color .15s', opacity:loading?.6:1 }}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=C.cyan}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.b1}>
                  <div style={{ fontSize:20, marginBottom:6 }}>{s.icon}</div>
                  <div style={{ fontFamily:'JetBrains Mono,monospace', fontWeight:700, fontSize:11, color:C.t1, marginBottom:3 }}>{s.name}</div>
                  <div style={{ fontSize:8.5, color:C.t4, lineHeight:1.5 }}>{s.desc}</div>
                </button>
              ))}
            </div>
          )}

          {showHF && (
            <HFDatasetPicker
              accentColor={C.cyan}
              onClose={() => setShowHF(false)}
              onLoaded={(csv, name, size) => ingest(csv, name, size)}
            />
          )}

          {error && <div style={{ marginTop:12, padding:'9px 14px', background:`${C.red}10`, border:`1px solid ${C.red}40`, borderRadius:4, fontSize:10, color:C.red }}>{error}</div>}
        </div>
      </div>

      {/* Feature grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginTop:28, maxWidth:720, width:'100%' }}>
        {[
          ['🧹','Cleaning','11 ops, undo/redo, version history, CSV export'],
          ['📊','Distributions','All-column overview + single deep-dive'],
          ['📐','Statistics','Descriptive, inferential, group comparison'],
          ['🔗','Correlations','Heatmap, ranked pairs, scatter gallery'],
          ['⬟','Visualization','Auto charts, slicers, custom builder'],
          ['💡','Quality','Auto-written analysis, feature flags'],
          ['📄','Report','Drag sections, notes, HTML/MD export'],
          ['⚡','Instant','Pure JS, no server, no LLM needed'],
        ].map(([icon,label,desc])=>(
          <div key={label} style={{ padding:'12px', background:C.surface, border:`1px solid ${C.b1}`, borderRadius:5 }}>
            <div style={{ fontSize:16, marginBottom:5 }}>{icon}</div>
            <div style={{ fontSize:10, color:C.t1, fontWeight:600, marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:8.5, color:C.t4, lineHeight:1.5 }}>{desc}</div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TOP BAR — sticky, with per-tab health dots
// ═══════════════════════════════════════════════════════════════════════════
function TopBar({ ds, profile, activeTab, setActiveTab, onReset, onPublish, onCheckpoint, checkpointing, isAuthenticated, saveVersion }) {
  const { qualityScore, meta, nullPct, dupCount } = profile;
  const { startTransition } = React;

  // Badge counts per tab (drives the warning dots)
  const tabBadge = {
    profile:      null,
    cleaning:     (nullPct > 0 ? 1 : 0) + (dupCount > 0 ? 1 : 0) || null,
    distributions:null,
    statistics:   null,
    correlations: meta.numeric < 2 ? '!' : null,
    visualization: null,
    quality:      profile.quality.filter(i=>i.icon==='⚠'||i.icon==='✗').length || null,
    report:       null,
  };

  return (
    <div style={{ position:'sticky', top:0, zIndex:200, background:C.deep, borderBottom:`1px solid ${C.b0}` }}>
      {/* File info */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 18px', borderBottom:`1px solid ${C.b0}` }}>
        <button onClick={onReset} style={{ padding:'4px 10px', background:C.surface, border:`1px solid ${C.b1}`, color:C.t3, fontFamily:'JetBrains Mono,monospace', fontSize:8, borderRadius:3, cursor:'pointer', letterSpacing:'.07em' }}>← NEW</button>
        <div style={{ width:1, height:14, background:C.b1 }}/>
        <span style={{ fontSize:11, color:C.t1, fontWeight:600 }}>{ds.name}</span>
        <span style={{ fontSize:8.5, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>{meta.rows.toLocaleString()} × {meta.cols}</span>
        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
          <Pill label={`${meta.numeric} numeric`}      color={C.cyan}/>
          <Pill label={`${meta.categorical} cat`}      color='#fbbf24'/>
          {nullPct > 0 && <Pill label={`${nullPct}% null`}  color={nullPct>20?C.red:'#fbbf24'}/>}
          {dupCount > 0 && <Pill label={`${dupCount} dupes`} color='#fbbf24'/>}
          {meta.rows > ANALYTICS_CAP && <Pill label={`⚡ stats from ${(ANALYTICS_CAP/1000).toFixed(0)}K sample`} color={C.violet}/>}
        </div>
        <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:7, height:7, borderRadius:'50%', background:qualityScore>=80?C.green:qualityScore>=60?'#fbbf24':C.red, boxShadow:`0 0 7px ${qualityScore>=80?C.green:qualityScore>=60?'#fbbf24':C.red}` }}/>
          <span style={{ fontSize:8.5, color:C.t4, fontFamily:'JetBrains Mono,monospace' }}>QUALITY {qualityScore}/100</span>
          {isAuthenticated && (
            <>
              <button onClick={onCheckpoint} disabled={checkpointing} style={{ padding:'4px 12px', background:`${C.green}18`, border:`1px solid ${C.green}50`, color:C.green, fontFamily:'JetBrains Mono,monospace', fontSize:8, borderRadius:3, cursor: checkpointing ? 'wait' : 'pointer', letterSpacing:'.07em', fontWeight:700, marginLeft:4, opacity: checkpointing ? 0.6 : 1 }}>
                {checkpointing ? '⏳ SAVING…' : saveVersion > 0 ? `💾 SAVE VERSION [${saveVersion}]` : '💾 SAVE VERSION'}
              </button>
              <button onClick={onPublish} style={{ padding:'4px 12px', background:`${C.cyan}18`, border:`1px solid ${C.cyan}50`, color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontSize:8, borderRadius:3, cursor:'pointer', letterSpacing:'.07em', fontWeight:700, marginLeft:4 }}>
                📤 PUBLISH
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', overflowX:'auto', padding:'0 6px' }}>
        {TABS.map(tab => {
          const active  = activeTab === tab.id;
          const badge   = tabBadge[tab.id];
          return (
            <button key={tab.id} onClick={()=>startTransition(()=>setActiveTab(tab.id))} style={{ display:'flex', alignItems:'center', gap:5, padding:'10px 13px', border:'none', background:'transparent', color:active?tab.color:C.t3, borderBottom:`2px solid ${active?tab.color:'transparent'}`, fontFamily:'JetBrains Mono,monospace', fontSize:9, fontWeight:active?700:400, cursor:'pointer', whiteSpace:'nowrap', letterSpacing:'.08em', transition:'all .1s', flexShrink:0, position:'relative' }}>
              <span style={{ fontSize:12 }}>{tab.icon}</span>
              <span>{tab.label}</span>
              {badge != null && <span style={{ fontSize:7, background:C.red, color:'#fff', borderRadius:10, padding:'1px 4px', fontWeight:700, minWidth:14, textAlign:'center' }}>{badge}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function AutoEDA({ onBack, isAuthenticated, authUser, workspaceRestore, onWorkspaceRestored }) {
  const [phase,          setPhase]          = useState('upload');
  const [ds,             setDs]             = useState(null);
  const [profile,        setProfile]        = useState(null);
  const [activeTab,      setActiveTab]      = useState('profile');
  const [dashCards,      setDashCards]      = useState([]);
  const [reportState,    setReportState]    = useState(null);
  const [deferredUrl,    setDeferredUrl]    = useState('');
  const [deferredCsv,    setDeferredCsv]    = useState(null);
  const [publishOpen,    setPublishOpen]    = useState(false);
  const [checkpointing,  setCheckpointing]  = useState(false);
  const [workspaceId,    setWorkspaceId]    = useState(null);
  const [saveVersion,    setSaveVersion]    = useState(0);
  const [baseVersion,    setBaseVersion]    = useState(0);
  const [showSaveInfo,   setShowSaveInfo]   = useState(false);
  const [snapshotOnly,   setSnapshotOnly]   = useState(false);
  // Worker state — null = idle, object = actively processing
  const [workerProgress, setWorkerProgress] = useState(null);

  const workerRef          = useRef(null);   // the Web Worker instance
  const isProcessingRef    = useRef(false);  // guards against stale DONE after reset
  const pendingNameRef     = useRef('');     // filename for the in-flight request
  const pendingSizeRef     = useRef(0);      // file size in bytes for processing screen
  const profileTimerRef    = useRef(null);   // debounce timer for post-cleaning re-profile
  const pendingProfileDsRef = useRef(null);  // latest ds waiting for re-profile

  const userKey = getAuthUserKey(authUser);
  const resetWorkspaceTracking = useCallback(() => {
    setWorkspaceId(null);
    setSaveVersion(0);
    setBaseVersion(0);
    setSnapshotOnly(false);
  }, []);
  const persistLocal = useCallback((data) => {
    const wsId = data?.workspaceId;
    idbSave(wsId ? `auto_eda:${wsId}` : AUTO_EDA_IDB_KEY, data).catch(() => {});
  }, []);

  // IDB auto-restore removed: every left-panel click = fresh start.
  // Workspace resume (from homepage) is handled below.
  const idbRestoredRef = useRef(false);

  // ── Handle workspace restore from WorkspaceDock ──────────────────────────
  useEffect(() => {
    if (!workspaceRestore || workspaceRestore.module_id !== 'auto_eda') return;
    idbRestoredRef.current = true;
    const desiredTab = workspaceRestore.active_tab;
    const requestedVersion = Number(workspaceRestore.version_no || 0) || null;

    // Fetch snapshot from backend, check local IDB for matching rows
    const fetchAndRestore = async () => {
      try {
        const [result, perWsLocal, flatLocal] = await Promise.all([
          requestedVersion && workspaceRestore.id
            ? workspaceApi.get(workspaceRestore.id, { version: requestedVersion })
            : workspaceApi.get(workspaceRestore.id),
          idbLoad(`auto_eda:${workspaceRestore.id}`),
          idbLoad(AUTO_EDA_IDB_KEY),
        ]);
        // Per-workspace key takes priority; flat key used for backward compat.
        const localSaved = perWsLocal || (flatLocal?.workspaceId === workspaceRestore.id ? flatLocal : null);
        const version = result?.workspace?.selected_version;
        const snap = version?.snapshot;
        const ws = result?.workspace;
        if (snap?.profile) {
          const expectedHeaders = snap.ds?.headers || [];
          // Only use IDB rows when they belong to this workspace (workspaceId-matched).
          const rawLocalRows = localSaved?.ds?.rows || [];
          const localRows = validateRowHeaders(rawLocalRows, expectedHeaders);
          const nextDs = { name: snap.ds?.name || '', headers: expectedHeaders, rows: localRows };
          const nextActiveTab = desiredTab || snap.activeTab || 'profile';
          const nextVersion = version.version_no || requestedVersion || 1;
          setDs(nextDs);
          setProfile(snap.profile);
          setDashCards(snap.dashCards || []);
          setActiveTab(nextActiveTab);
          setWorkspaceId(workspaceRestore.id);
          setSaveVersion(nextVersion);
          setBaseVersion(nextVersion);
          setSnapshotOnly(localRows.length === 0);
          setPhase('report');
          persistLocal({ ds: nextDs, profile: snap.profile, dashCards: snap.dashCards || [], activeTab: nextActiveTab, workspaceId: workspaceRestore.id, saveVersion: nextVersion, baseVersion: nextVersion });
        } else if (ws) {
          // Server snapshot null (too large or old save). Tier 2: check IDB.
          if (localSaved?.profile) {
            const nextActiveTab = desiredTab || localSaved.activeTab || 'profile';
            const nextVersion = localSaved.saveVersion || version?.version_no || 1;
            setDs(localSaved.ds || null);
            setProfile(localSaved.profile);
            setDashCards(localSaved.dashCards || []);
            setActiveTab(nextActiveTab);
            setWorkspaceId(workspaceRestore.id);
            setSaveVersion(nextVersion);
            setBaseVersion(nextVersion);
            setSnapshotOnly(!(localSaved.ds?.rows?.length > 0));
            setPhase('report');
          }
          // Tier 3: nothing locally — phase stays 'upload', workspaceId not set
          // (user must re-upload; a fresh save will create a new workspace entry).
        }
      } catch {}
      onWorkspaceRestored?.();
    };
    fetchAndRestore();
  }, [workspaceRestore]);

  // ── Create worker once on mount, terminate on unmount ──────────────────
  useEffect(() => {
    const worker = new Worker(new URL('./workers/edaWorker.js', import.meta.url));

    worker.onmessage = (e) => {
      const msg = e.data;

      if (msg.type === 'PROGRESS') {
        setWorkerProgress(msg);
        return;
      }

      if (msg.type === 'ERROR') {
        setWorkerProgress({ phase: 'error', message: msg.message, pct: 0 });
        isProcessingRef.current = false;
        return;
      }

      if (msg.type === 'DONE') {
        // Guard: ignore if user reset while worker was still running
        if (!isProcessingRef.current) return;
        isProcessingRef.current = false;

        const { headers, rows, profile: p } = msg;
        const name = pendingNameRef.current || 'dataset.csv';

        // Usage / analytics tracking (same as before)
        startUsageSession('auto_eda');
        usageStep('auto_eda', 'upload', { name, rows: rows.length }, AUTO_EDA_USAGE_WORKFLOW);
        trackAction({
          module: 'auto_eda', type: 'auto_eda:upload',
          label: `Upload: ${name}`, group: 'data', icon: '📤',
          params: {}, replayable: false,
          meta: { name, rows: rows.length, cols: headers.length },
        });
        window.__dd_ctx = {
          datasetName: name, rowCount: rows.length, colCount: headers.length,
          qualityScore: p?.qualityScore,
          topInsights: p?.quality?.slice(0, 3).map(i => i.text) || [],
          currentTab: 'profile', module: 'auto_eda',
        };
        if (isAuthenticated) {
          addWorkspaceItem(userKey, 'auto_eda', {
            kind: 'report', title: name,
            meta: { rows: rows.length, cols: headers.length, qualityScore: p?.qualityScore ?? null, nullPct: p?.nullPct ?? null, dupCount: p?.dupCount ?? null },
          });
        }

        setDs({ name, headers, rows });
        setProfile(p);
        setWorkerProgress(null);
        setPhase('report');
        setActiveTab('profile');
        // Persist to IndexedDB — raw rows stay local, never sent to server
        persistLocal({ ds: { name, headers, rows }, profile: p, dashCards: [], activeTab: 'profile', workspaceId: null, saveVersion: 0, baseVersion: 0 });
      }
    };

    worker.onerror = (err) => {
      setWorkerProgress({ phase: 'error', message: err.message || 'Worker crashed.', pct: 0 });
      isProcessingRef.current = false;
    };

    workerRef.current = worker;
    return () => worker.terminate();
  }, [isAuthenticated]);

  const buildSnapshot = useCallback(() => {
    if (!ds || !profile) return {};
    return {
      dataset: {
        name: ds.name || '',
        rows: ds.rows.length,
        cols: ds.headers.length,
        numeric: profile.numCols.length,
        categorical: profile.catCols.length,
      },
      metrics: {
        null_pct: `${profile.nullPct}%`,
        duplicates: String(profile.dupCount),
        numeric_cols: String(profile.numCols.length),
        categorical_cols: String(profile.catCols.length),
        top_correlation: profile.topCorr.length > 0
          ? `${profile.topCorr[0].a} ↔ ${profile.topCorr[0].b} (r=${profile.topCorr[0].r.toFixed(3)})`
          : 'n/a',
      },
      insights: (profile.quality || []).slice(0, 6).map(i => i.text || '').filter(Boolean),
      tabs_completed: TABS.map(t => t.id),
      quality_score: profile.qualityScore,
      // Rich report data
      col_headers: ds.headers.slice(0, 60),
      sample_rows: ds.rows.slice(0, 50).map(r =>
        Array.isArray(r) ? r.slice(0, 60) : ds.headers.slice(0, 60).map(h => r[h] ?? null)
      ),
      col_profiles: (profile.colProfiles || []).slice(0, 50).map(p => ({
        col: p.col,
        type: p.type,
        uniqueCount: p.uniqueCount,
        nullPct: p.nullPct,
        stats: p.stats ? {
          mean:   +Number(p.stats.mean).toFixed(4),
          std:    +Number(p.stats.std).toFixed(4),
          min:    +Number(p.stats.min).toFixed(4),
          max:    +Number(p.stats.max).toFixed(4),
          median: +Number(p.stats.median).toFixed(4),
          skew:   +Number(p.stats.skew).toFixed(4),
        } : null,
        // Store fresh full-dataset values in the existing Lambda-passable fields:
        // hist → 20-bin from full ds.rows (same as live Report tab uses)
        // valueCounts → top 30 entries (covers DonutChart×12, HBarChart×30, RadialChart×12)
        valueCounts: p.type === 'categorical'
          ? getValueCounts(ds.rows, p.col, 30).map(vc => ({ v: vc.name, n: vc.count }))
          : [],
        hist: p.type === 'numeric'
          ? getHistogram(ds.rows, p.col, 20).map((b, i, arr) => ({
              x0: b.label,
              x1: arr[i + 1]?.label ?? b.label,
              n:  b.count,
            }))
          : [],
        // Also store in chart-ready format (used once Lambda is deployed with updated handler.py)
        histChart:        p.type === 'numeric'     ? getHistogram(ds.rows, p.col, 20)  : null,
        valueCountsChart: p.type === 'categorical' ? getValueCounts(ds.rows, p.col, 30) : null,
        shape:       p.shape || null,
        flags:       (p.flags || []).map(f => f.label || f),
      })),
      correlations: (profile.topCorr || []).slice(0, 15).map(c => ({
        a: c.a, b: c.b, r: +Number(c.r).toFixed(4),
      })),
      corr_scatter: (profile.topCorr || []).slice(0, 6).map(c => {
        const pts = ds.rows
          .map(r => {
            const x = parseFloat(r[c.a]), y = parseFloat(r[c.b]);
            return (!isNaN(x) && !isNaN(y)) ? [+x.toFixed(4), +y.toFixed(4)] : null;
          })
          .filter(Boolean);
        return { a: c.a, b: c.b, pts };
      }),
      quality_insights: (profile.quality || []).map(q => q.text || '').filter(Boolean),
      null_map: Object.fromEntries(
        Object.entries(profile.nullMap || {}).slice(0, 50)
      ),
      dashboard_cards: (dashCards || []).slice(0, 20)
        .filter(c => c?.chart?.type)
        .map(c => ({
          id: c.id,
          chart: {
            id:       c.chart.id,
            type:     c.chart.type,
            col:      c.chart.col      || null,
            xCol:     c.chart.xCol     || null,
            yCol:     c.chart.yCol     || null,
            catCol:   c.chart.catCol   || null,
            numCol:   c.chart.numCol   || null,
            cols:     c.chart.cols     || [],
            agg:      c.chart.agg      || 'mean',
            title:    c.chart.title    || null,
            subtitle: c.chart.subtitle || null,
            dateCol:  c.chart.dateCol  || null,
            valueCol: c.chart.valueCol || null,
          },
          annotation: typeof c.annotation === 'string' ? c.annotation : null,
        })),
      report_state: reportState,
      viz_cards: (() => {
        try {
          const typesArr = (profile.colProfiles || []).map(p => ({
            col: p.col, type: p.type, uniqueCount: p.uniqueCount || 0,
          }));
          const NOT_SCATTER = t => t !== 'scatter' && t !== 'bubble';
          const NOT_DIST    = t => !['hist','donut','pie','hbar','radial','num_overview'].includes(t);
          return buildAutoCharts(ds.rows, ds.headers, typesArr)
            .filter(c => NOT_SCATTER(c.type) && NOT_DIST(c.type))
            .slice(0, 18)
            .map(c => {
              const chart = {
                type:     c.type,
                col:      c.col      || null,
                xCol:     c.xCol     || null,
                yCol:     c.yCol     || null,
                catCol:   c.catCol   || null,
                numCol:   c.numCol   || null,
                cols:     c.cols     || [],
                agg:      c.agg      || 'mean',
                title:    c.title    || null,
                subtitle: c.subtitle || null,
                dateCol:  c.dateCol  || null,
                valueCol: c.valueCol || null,
                sizeCol:  c.sizeCol  || null,
              };
              const col = c.col || c.xCol;
              let data = null;
              if (['treemap','funnel'].includes(c.type)) {
                data = getValueCounts(ds.rows, col, 30);
              } else if (['groupby','bar','line','area'].includes(c.type)) {
                data = computeAggrData(ds.rows, c.xCol || c.catCol, c.yCol || c.numCol, c.agg || 'mean');
              } else if (c.type === 'boxplot') {
                const vals = ds.rows.map(r => parseFloat(r[col])).filter(v => !isNaN(v)).sort((a, b) => a - b);
                if (vals.length >= 5) {
                  const n = vals.length;
                  const q1 = vals[Math.floor(n * 0.25)], med = vals[Math.floor(n * 0.5)], q3 = vals[Math.floor(n * 0.75)];
                  const iqr = q3 - q1;
                  const wLo = vals.find(v => v >= q1 - 1.5 * iqr) ?? vals[0];
                  const wHi = [...vals].reverse().find(v => v <= q3 + 1.5 * iqr) ?? vals[n - 1];
                  data = {
                    box: [+wLo.toFixed(4), +q1.toFixed(4), +med.toFixed(4), +q3.toFixed(4), +wHi.toFixed(4)],
                    outliers: vals.filter(v => v < q1 - 1.5 * iqr || v > q3 + 1.5 * iqr).slice(0, 80).map(v => +v.toFixed(4)),
                  };
                }
              } else if (c.type === 'multibox') {
                data = (c.cols || []).map(mc => {
                  const vals = ds.rows.map(r => parseFloat(r[mc])).filter(v => !isNaN(v)).sort((a, b) => a - b);
                  if (vals.length < 5) return null;
                  const n = vals.length;
                  const q1 = vals[Math.floor(n * 0.25)], med = vals[Math.floor(n * 0.5)], q3 = vals[Math.floor(n * 0.75)];
                  const iqr = q3 - q1;
                  const wLo = vals.find(v => v >= q1 - 1.5 * iqr) ?? vals[0];
                  const wHi = [...vals].reverse().find(v => v <= q3 + 1.5 * iqr) ?? vals[n - 1];
                  return { col: mc, box: [+wLo.toFixed(4), +q1.toFixed(4), +med.toFixed(4), +q3.toFixed(4), +wHi.toFixed(4)] };
                }).filter(Boolean);
              } else if (c.type === 'timeseries') {
                const MAX_TS = 150;
                const raw = ds.rows
                  .map(r => ({ t: new Date(r[c.dateCol]).getTime(), v: parseFloat(r[c.valueCol]) }))
                  .filter(p => !isNaN(p.t) && !isNaN(p.v))
                  .sort((a, b) => a.t - b.t);
                if (raw.length) {
                  const step = raw.length > MAX_TS ? Math.ceil(raw.length / MAX_TS) : 1;
                  data = raw.filter((_, i) => i % step === 0).map(p => [new Date(p.t).toLocaleDateString(), +p.v.toFixed(4)]);
                }
              } else if (c.type === 'parallel') {
                const validCols = (c.cols || []).slice(0, 8);
                data = ds.rows
                  .map(r => validCols.map(mc => { const v = parseFloat(r[mc]); return isNaN(v) ? null : +v.toFixed(4); }))
                  .filter(row => row.every(v => v !== null));
              }
              return { chart, data };
            });
        } catch (_) {
          return [];
        }
      })(),
    };
  }, [ds, profile, dashCards, reportState]);

  const doSave = useCallback(async () => {
    if (!ds || !profile || !isAuthenticated) return;
    setCheckpointing(true);
    try {
      const payload = {
        parent_version: baseVersion || saveVersion || undefined,
        active_tab: activeTab,
        summary: {
          filename: ds.name || '',
          rows: ds.rows.length,
          cols: ds.headers.length,
          best_score: profile.qualityScore,
          note: `Quality ${profile.qualityScore}/100 · ${profile.nullPct}% null`,
        },
        meta: { tabs_visited: TABS.map(t => t.id) },
        actions: getWorkspaceActionSummary('auto_eda', authUser),
      };
      let newId = workspaceId;
      let nextVersion = saveVersion;
      if (!workspaceId) {
        const result = await workspaceApi.save({
          module_id: 'auto_eda', label: ds.name || 'Auto EDA', ...payload,
          snapshot: { ds: { name: ds.name, headers: ds.headers }, profile, dashCards, activeTab },
        });
        newId = result.id;
        nextVersion = result.version_no || 1;
        setWorkspaceId(newId);
      } else {
        const result = await workspaceApi.update(workspaceId, {
          ...payload,
          snapshot: { ds: { name: ds.name, headers: ds.headers }, profile, dashCards, activeTab },
        });
        nextVersion = result.version_no || (saveVersion + 1);
      }
      setSaveVersion(nextVersion);
      setBaseVersion(nextVersion);
      setSnapshotOnly(false);
      const localState = { ds, profile, dashCards, activeTab, workspaceId: newId, saveVersion: nextVersion, baseVersion: nextVersion };
      persistLocal(localState);
      window.dispatchEvent(new CustomEvent('dd-workspace-saved'));
    } catch (e) {
      // silent — best-effort
    } finally {
      setCheckpointing(false);
    }
  }, [ds, profile, dashCards, activeTab, workspaceId, saveVersion, baseVersion, isAuthenticated, authUser, persistLocal]);

  const handleCheckpoint = useCallback(() => {
    if (saveVersion === 0) { setShowSaveInfo(true); } else { doSave(); }
  }, [saveVersion, doSave]);

  // File ready — hand raw text to the Web Worker (main thread stays unblocked)
  const handleFileReady = useCallback((text, name, sizeBytes = 0) => {
    pendingNameRef.current  = name;
    pendingSizeRef.current  = sizeBytes;
    window.dispatchEvent(new CustomEvent('dd-collapse-left'));
    isProcessingRef.current = true;
    resetWorkspaceTracking();
    setDashCards([]);
    setReportState(null);
    setActiveTab('profile');
    setDeferredUrl('');
    setDeferredCsv(null);
    setPhase('processing');
    setWorkerProgress({ phase: 'starting', message: 'Starting analysis engine…', pct: 1 });
    workerRef.current.postMessage({ type: 'PROFILE', csvText: text, fileName: name });
  }, [resetWorkspaceTracking]);

  // Best-effort step tracking (idempotent on backend).
  useEffect(() => {
    if (phase !== 'report') return;
    const step = USAGE_STEP_BY_TAB[activeTab];
    if (step) usageStep('auto_eda', step, null, AUTO_EDA_USAGE_WORKFLOW);
    trackAction({
      module: 'auto_eda',
      type: 'auto_eda:tab_switch',
      label: `View ${activeTab}`,
      group: 'explore',
      icon: '🗂',
      params: { tab: activeTab },
      replayable: false,
    });
    if (typeof window !== 'undefined' && window.__dd_ctx) window.__dd_ctx.currentTab = activeTab;
  }, [phase, activeTab]);

  // CleaningLab pushes every op here — ALL tabs instantly see cleaned data
  const handleDsUpdate = useCallback(updatedDs => {
    setDs(updatedDs);
    // Debounce re-profile: batch rapid cleaning ops into a single profile recompute
    pendingProfileDsRef.current = updatedDs;
    if (profileTimerRef.current) clearTimeout(profileTimerRef.current);
    profileTimerRef.current = setTimeout(() => {
      setProfile(profileDataset(pendingProfileDsRef.current));
      profileTimerRef.current = null;
    }, 300);
  }, []);

  // Autosave to IDB whenever state changes (debounced 5 s)
  const autosaveTimerRef = useRef(null);
  useEffect(() => {
    if (!ds || !profile) return;
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      persistLocal({ ds, profile, dashCards, activeTab, workspaceId, saveVersion, baseVersion });
    }, 5_000);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [ds, profile, dashCards, activeTab, workspaceId, saveVersion, baseVersion, persistLocal]);

  // Resets UI state back to upload screen. Never deletes IDB — the saved
  // session persists so RESUME still works after clicking ← NEW.
  // IDB is only overwritten when a new dataset finishes processing.
  const reset = useCallback(() => {
    isProcessingRef.current = false;
    setPhase('upload'); setDs(null); setProfile(null);
    setActiveTab('profile'); setDashCards([]); setReportState(null);
    setWorkerProgress(null);
    setDeferredUrl('');
    setDeferredCsv(null);
    resetWorkspaceTracking();
  }, [resetWorkspaceTracking]);

  // Same as reset — kept as alias so event handlers are explicit about intent.
  const softReset = useCallback(() => {
    isProcessingRef.current = false;
    setPhase('upload'); setDs(null); setProfile(null);
    setActiveTab('profile'); setDashCards([]); setReportState(null);
    setWorkerProgress(null);
    resetWorkspaceTracking();
  }, [resetWorkspaceTracking]);

  const pinnedIds = useMemo(() => new Set(dashCards.map(c => c.chart?.id)), [dashCards]);
  const pinChart = useCallback((chart) => {
    if (!chart) return;
    setDashCards(prev => {
      const exists = prev.some(c => c.chart?.id === chart.id);
      if (exists) return prev.filter(c => c.chart?.id !== chart.id);
      const idx = prev.length;
      const x = 20 + (idx % 3) * 390;
      const y = 20 + Math.floor(idx / 3) * 430;
      return [...prev, { id: `pin_${Date.now()}_${chart.id}`, chart, pos: { x, y, w: 360 }, annotation: null }];
    });
  }, []);

  // Right panel actions:
  // - dd:auto_eda:new
  // - dd:auto_eda:load_url { url }
  // - dd:auto_eda:load_csv { name, csvText }
  // - dd:auto_eda:goto { tab }
  useEffect(() => {
    const onNew = () => reset();
    const onLoadUrl = (ev) => {
      const url = ev?.detail?.url;
      if (!url) return;
      setDeferredUrl(String(url));
      setDeferredCsv(null);
      softReset();
    };
    const onLoadCsv = (ev) => {
      const csvText = ev?.detail?.csvText;
      if (!csvText) return;
      const name = ev?.detail?.name || 'dataset.csv';
      setDeferredUrl('');
      setDeferredCsv({ id: Date.now(), name, csvText: String(csvText) });
      softReset();
    };
    const onGoto = (ev) => {
      const nextTab = typeof ev?.detail === 'string' ? ev.detail : ev?.detail?.tab;
      if (!nextTab) return;
      if (!TABS.some(tab => tab.id === nextTab)) return;
      setActiveTab(nextTab);
    };
    window.addEventListener('dd:auto_eda:new', onNew);
    window.addEventListener('dd:auto_eda:load_url', onLoadUrl);
    window.addEventListener('dd:auto_eda:load_csv', onLoadCsv);
    window.addEventListener('dd:auto_eda:goto', onGoto);
    return () => {
      window.removeEventListener('dd:auto_eda:new', onNew);
      window.removeEventListener('dd:auto_eda:load_url', onLoadUrl);
      window.removeEventListener('dd:auto_eda:load_csv', onLoadCsv);
      window.removeEventListener('dd:auto_eda:goto', onGoto);
    };
  }, [reset, softReset]);

  if (workspaceRestore && workspaceRestore.module_id === 'auto_eda') return (
    <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--void)' }}>
      <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, color:'var(--t3)', letterSpacing:'.06em' }}>⏳ RESTORING WORKSPACE…</div>
    </div>
  );

  if (phase === 'upload') return <UploadScreen onFileReady={handleFileReady} autoUrl={deferredUrl} autoCsv={deferredCsv} />;

  // Processing phase — worker is running; show rich progress UI (main thread stays responsive)
  if (phase === 'processing') {
    const pct  = workerProgress?.pct ?? 0;
    const msg  = workerProgress?.message ?? 'Starting…';
    const wPhase = workerProgress?.phase ?? 'starting';
    const rowCount = workerProgress?.rowCount;
    const colCount = workerProgress?.colCount;
    const isError  = wPhase === 'error';

    const STEPS = [
      { id: 'parse',    label: 'PARSE',    done: pct >= 30, active: pct < 30 && !isError },
      { id: 'profile',  label: 'PROFILE',  done: pct >= 87, active: pct >= 30 && pct < 87 },
      { id: 'quality',  label: 'QUALITY',  done: pct >= 95, active: pct >= 87 && pct < 95 },
      { id: 'finalize', label: 'FINALIZE', done: pct >= 99, active: pct >= 95 },
    ];

    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.void, padding:40 }}>
        <style>{`@keyframes eda-spin { to { transform: rotate(360deg); } } @keyframes eda-pulse { 0%,100%{opacity:.5}50%{opacity:1} }`}</style>
        <div style={{ width:'100%', maxWidth:520, display:'flex', flexDirection:'column', gap:28 }}>

          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            {isError
              ? <div style={{ fontSize:28 }}>⚠</div>
              : <div style={{ width:36, height:36, border:`2.5px solid ${C.b1}`, borderTop:`2.5px solid ${C.cyan}`, borderRadius:'50%', animation:'eda-spin .75s linear infinite', flexShrink:0 }}/>
            }
            <div>
              <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:10, letterSpacing:'.18em', color: isError ? C.red : C.cyan, fontWeight:700 }}>
                {isError ? 'ANALYSIS ERROR' : 'ANALYZING DATASET'}
              </div>
              <div style={{ fontSize:11, color:C.t2, marginTop:3 }}>{pendingNameRef.current || 'dataset.csv'}</div>
            </div>
          </div>

          {/* Row / col count (appears once parsing is done) */}
          {rowCount && (
            <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
              {[
                { label:'ROWS',        v: rowCount.toLocaleString() },
                { label:'COLUMNS',     v: colCount },
                { label:'SIZE',        v: fmtBytes(pendingSizeRef.current) },
                { label:'TOTAL CELLS', v: (rowCount * colCount).toLocaleString() },
              ].map(m => (
                <div key={m.label} style={{ padding:'8px 14px', background:C.surface, border:`1px solid ${C.b1}`, borderRadius:3 }}>
                  <div style={{ fontSize:7.5, color:C.t4, fontFamily:'JetBrains Mono,monospace', letterSpacing:'.12em' }}>{m.label}</div>
                  <div style={{ fontFamily:'Syne,sans-serif', fontWeight:900, fontSize:20, color:C.cyan }}>{m.v}</div>
                </div>
              ))}
            </div>
          )}

          {/* Steps row */}
          <div style={{ display:'flex', alignItems:'center', gap:0 }}>
            {STEPS.map((step, i) => {
              const clr = step.done ? C.green : step.active ? C.cyan : C.t4;
              return (
                <React.Fragment key={step.id}>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                    <div style={{ width:22, height:22, borderRadius:'50%', border:`2px solid ${clr}`, background: step.done ? C.green : step.active ? `${C.cyan}20` : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', transition:'all .3s' }}>
                      {step.done
                        ? <span style={{ fontSize:10, color:C.void, fontWeight:900 }}>✓</span>
                        : step.active
                          ? <div style={{ width:7, height:7, borderRadius:'50%', background:C.cyan, animation:'eda-pulse 1s ease-in-out infinite' }}/>
                          : null
                      }
                    </div>
                    <span style={{ fontSize:7.5, fontFamily:'JetBrains Mono,monospace', color:clr, letterSpacing:'.1em', fontWeight:step.active?700:400 }}>{step.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div style={{ flex:1, height:2, background: STEPS[i+1].done || STEPS[i+1].active ? C.cyan : C.b1, margin:'0 6px 18px', transition:'background .3s' }}/>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Progress bar */}
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ fontSize:9, color: isError ? C.red : C.t3, fontFamily:'JetBrains Mono,monospace' }}>
                {isError ? msg : msg}
              </span>
              <span style={{ fontSize:9, color:C.cyan, fontFamily:'JetBrains Mono,monospace', fontWeight:700 }}>{Math.round(pct)}%</span>
            </div>
            <div style={{ height:4, background:C.b1, borderRadius:2, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${pct}%`, background: isError ? C.red : `linear-gradient(90deg,${C.cyan},${C.violet})`, borderRadius:2, transition:'width .4s ease' }}/>
            </div>
          </div>

          {/* Error action */}
          {isError && (
            <button onClick={reset} style={{ padding:'9px 20px', background:`${C.red}18`, border:`1px solid ${C.red}50`, color:C.red, fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer', letterSpacing:'.1em', alignSelf:'flex-start' }}>
              ← TRY AGAIN
            </button>
          )}

          {/* Privacy note */}
          {!isError && (
            <div style={{ fontSize:8.5, color:C.t4, fontFamily:'JetBrains Mono,monospace', borderTop:`1px solid ${C.b0}`, paddingTop:16, letterSpacing:'.05em' }}>
              🔒 Your data never leaves this browser — all computation runs locally.
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!ds || !profile)   return null;

  // ReportComposer expects experiment shape
  const experiment = { ds, name:ds.name, type:'structured' };

  return (
    <PinContext.Provider value={{ pinChart, pinnedIds }}>
      <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh', background:C.void }}>
        {showSaveInfo && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ background:'#111', border:`1px solid ${C.green}40`, borderRadius:8, padding:'28px 32px', maxWidth:440, fontFamily:'JetBrains Mono,monospace' }}>
              <div style={{ color:C.green, fontSize:13, fontWeight:700, marginBottom:12 }}>💾 SAVE VERSION</div>
              <div style={{ color:'#ccc', fontSize:11, lineHeight:1.8, marginBottom:20 }}>
                Raw uploaded data stays <strong style={{ color:'#fff' }}>in this browser</strong>.{' '}
                Saved versions store a lightweight analysis snapshot and workflow history on the server.{' '}
                On another device, you may still need to re-upload the same dataset to regain full interactivity.
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setShowSaveInfo(false)} style={{ padding:'6px 14px', background:'transparent', border:'1px solid #444', color:'#888', fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer' }}>
                  CANCEL
                </button>
                <button onClick={() => { setShowSaveInfo(false); doSave(); }} style={{ padding:'6px 14px', background:`${C.green}20`, border:`1px solid ${C.green}60`, color:C.green, fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer', fontWeight:700 }}>
                  GOT IT · SAVE
                </button>
              </div>
            </div>
          </div>
        )}
        <TopBar
          ds={ds} profile={profile}
          activeTab={activeTab} setActiveTab={setActiveTab}
          onReset={reset}
          onPublish={() => setPublishOpen(true)}
          onCheckpoint={handleCheckpoint}
          checkpointing={checkpointing}
          isAuthenticated={isAuthenticated}
          saveVersion={saveVersion}
        />

        {snapshotOnly && (
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 18px', background:'#fbbf2412', borderBottom:`1px solid #fbbf2435`, flexShrink:0, fontFamily:'JetBrains Mono,monospace', fontSize:10, color:'#fbbf24' }}>
            <span>&#9888;</span>
            <span>Viewing saved snapshot — raw data not in this browser. Profile stats &amp; quality are shown from last save. Re-upload your dataset to restore all features.</span>
            <button onClick={reset} style={{ marginLeft:'auto', padding:'3px 10px', background:'transparent', border:'1px solid #fbbf2450', color:'#fbbf24', fontFamily:'JetBrains Mono,monospace', fontSize:9, borderRadius:3, cursor:'pointer', whiteSpace:'nowrap' }}>
              ← RE-UPLOAD
            </button>
          </div>
        )}

        <div style={{ flex:1, padding:'20px 20px 60px', minWidth:0, maxWidth:'100%', overflowX:'hidden' }}>

        {activeTab==='profile' && (
          <ProfileTab ds={ds} profile={profile}/>
        )}

        {activeTab==='cleaning' && (
          <>
            <div style={{ marginBottom:12, padding:'9px 14px', background:`${C.green}09`, border:`1px solid ${C.green}28`, borderRadius:4, fontSize:10, color:C.t3, lineHeight:1.7 }}>
              <span style={{ color:C.green, fontWeight:700 }}>Live propagation</span> — every clean operation instantly updates Distributions, Statistics, Correlations and Visualization tabs.
              Use version history breadcrumb to jump between states. Download cleaned CSV or JSON at any time.
            </div>
            <CleaningLab ds={ds} onUpdate={handleDsUpdate}/>
          </>
        )}

        {activeTab==='distributions' && (
          <DistributionLab ds={ds}/>
        )}

        {activeTab==='statistics' && (
          <StatisticsLab ds={ds}/>
        )}

        {activeTab==='correlations' && (
          profile.meta.numeric >= 2
            ? <CorrelationLab ds={ds}/>
            : <div style={{ padding:60, textAlign:'center', color:C.t4, fontSize:12, background:C.surface, borderRadius:6, border:`1px solid ${C.b1}` }}>
                Correlation analysis requires at least 2 numeric columns.<br/>
                <span style={{ fontSize:10, color:C.t3, marginTop:6, display:'block' }}>Current dataset has {profile.meta.numeric} numeric column{profile.meta.numeric!==1?'s':''}.</span>
              </div>
        )}

        {activeTab==='visualization' && (
          <DashboardEngine
            ds={ds}
            initialCards={dashCards}
            onDashboardUpdate={setDashCards}
            enableCrossAnalysis
          />
        )}

        {activeTab==='quality' && (
          <QualityTab ds={ds} profile={profile}/>
        )}

        {activeTab==='report' && (
          <>
            <div style={{ marginBottom:12, padding:'9px 14px', background:`${C.violet}09`, border:`1px solid ${C.violet}28`, borderRadius:4, fontSize:10, color:C.t3, lineHeight:1.7 }}>
              <span style={{ color:C.violet, fontWeight:700 }}>Report reflects current data state</span> — cleaning operations in the Cleaning tab are applied here.
              Drag sections to reorder, add notes with tags, export as HTML or Markdown.
            </div>
            <ReportComposer experiment={experiment} dashboardCards={dashCards} profile={profile} reportState={reportState} onStateChange={setReportState}/>
          </>
        )}

        </div>
      </div>

      {publishOpen && (
        <PublishExperimentModal
          snapshot={buildSnapshot()}
          expType="auto_eda"
          onClose={() => setPublishOpen(false)}
          onPublished={() => setPublishOpen(false)}
        />
      )}
    </PinContext.Provider>
  );
}
